import type { DateRange } from '@daypicker/react';
import { addDays, differenceInCalendarDays, format, subDays } from 'date-fns';
import { ChevronDown, Download, FileSpreadsheet, FileText } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import toast from 'react-hot-toast';
import { AnalyticsDashboard } from '@/components/analytics/AnalyticsDashboard';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/NewButton';
import {
  type Granularity,
  useAnalyticsOverview,
  useMatterAnalytics,
  useUserAnalytics,
} from '@/hooks/useAnalytics';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { AdminTabContainer } from './ShellTabs';

const GRANULARITIES = ['day', 'week', 'month'] as const;

type ExportFormat = 'pdf' | 'excel';

const EXPORT_FORMATS: Record<ExportFormat, { label: string; extension: string; mimeType: string }> = {
  pdf: { label: 'PDF', extension: 'pdf', mimeType: 'application/pdf' },
  excel: {
    label: 'Excel',
    extension: 'xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
};

const DEFAULT_RANGE_DAYS = 90;

// Beyond this, daily buckets would render hundreds of bars.
const MAX_DAILY_RANGE_DAYS = 90;

/** Default bucket size for a newly selected range; the user can override it afterwards. */
const granularityFromRangeLength = (from: Date, to: Date): Granularity => {
  const days = differenceInCalendarDays(to, from);
  if (days > MAX_DAILY_RANGE_DAYS) return 'month';
  if (days > 31) return 'week';
  return 'day';
};

/**
 * Firm analytics dashboard as an Admin hub tab.
 * Reports Export button to the parent so it can live in the shell header.
 */
export function AnalyticsTab({
  onHeaderActionsChange,
}: {
  onHeaderActionsChange?: (actions: ReactNode | null) => void;
}) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => ({
    from: subDays(new Date(), DEFAULT_RANGE_DAYS),
    to: new Date(),
  }));
  const [granularity, setGranularity] = useState<Granularity>(() =>
    granularityFromRangeLength(subDays(new Date(), DEFAULT_RANGE_DAYS), new Date()),
  );
  const [isExporting, setIsExporting] = useState(false);

  const effectiveFrom = dateRange?.from ?? subDays(new Date(), 30);
  const effectiveTo = dateRange?.to ?? new Date();
  const dailyDisabled = differenceInCalendarDays(effectiveTo, effectiveFrom) > MAX_DAILY_RANGE_DAYS;

  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range.to) {
      setGranularity(granularityFromRangeLength(range.from, range.to));
    }
  }, []);

  const { data: unfilteredOverview } = useAnalyticsOverview({
    dateFrom: effectiveFrom,
    dateTo: effectiveTo,
    granularity,
    matterGuid: null,
    userGuid: null,
  });

  const { data: usersData, isLoading: usersLoading } = useUserAnalytics(
    { dateFrom: effectiveFrom, dateTo: effectiveTo, granularity },
  );

  const { data: mattersData, isLoading: mattersLoading } = useMatterAnalytics(
    { dateFrom: effectiveFrom, dateTo: effectiveTo, granularity },
  );

  const exportFrom = format(effectiveFrom, 'yyyy-MM-dd');
  const exportTo = format(effectiveTo, 'yyyy-MM-dd');

  const handleExport = useCallback(async (exportFormat: ExportFormat) => {
    setIsExporting(true);
    try {
      const params: Record<string, string> = {
        format: exportFormat,
        date_from: exportFrom,
        date_to: exportTo,
        granularity,
      };

      const response = await backendClient.post('/analytics/export', params, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: EXPORT_FORMATS[exportFormat].mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crimson-analytics-${exportFrom}-to-${exportTo}.${EXPORT_FORMATS[exportFormat].extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to export analytics');
    } finally {
      setIsExporting(false);
    }
  }, [exportFrom, exportTo, granularity]);

  const headerActions = useMemo(() => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" loading={isExporting} disabled={isExporting}>
          {!isExporting && <Download />}
          Export
          <ChevronDown className="opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => void handleExport('pdf')}>
          <FileText />
          {EXPORT_FORMATS.pdf.label}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void handleExport('excel')}>
          <FileSpreadsheet />
          {EXPORT_FORMATS.excel.label}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ), [handleExport, isExporting]);

  useEffect(() => {
    onHeaderActionsChange?.(headerActions);
    return () => onHeaderActionsChange?.(null);
  }, [headerActions, onHeaderActionsChange]);

  return (
    <AdminTabContainer>
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
        <DateRangePicker
          initialValue={dateRange}
          onChange={handleDateRangeChange}
          disabledDates={(date) => date > addDays(new Date(), 1)}
          footerPresets={['last7Days', 'last30Days', 'last90Days']}
          showTopControls
          triggerClassName="w-full justify-start sm:w-70"
        />

        <fieldset className="ml-auto flex overflow-hidden rounded-md border border-input">
          <legend className="sr-only">Granularity</legend>
          {GRANULARITIES.map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={granularity === g}
              disabled={g === 'day' && dailyDisabled}
              title={g === 'day' && dailyDisabled ? `Daily view is available for ranges up to ${MAX_DAILY_RANGE_DAYS} days` : undefined}
              onClick={() => setGranularity(g)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50',
                granularity === g
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-gray-600 hover:bg-gray-100 disabled:hover:bg-white',
              )}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </fieldset>
      </div>

      <AnalyticsDashboard
        dateFrom={effectiveFrom}
        dateTo={effectiveTo}
        granularity={granularity}
        unfilteredActiveUsersData={unfilteredOverview?.active_users_over_time}
        usersData={usersData}
        usersLoading={usersLoading}
        mattersData={mattersData}
        mattersLoading={mattersLoading}
      />
    </AdminTabContainer>
  );
}
